import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { queryDocs } from "@/integrations/firebase/data";
import { useAuth } from "@/hooks/use-auth";
import { isReallyLive, STALE_LIVE_MS } from "@/lib/calls";
import { useCallsSince, useNow } from "@/hooks/use-calls-since";
import { DataError } from "@/components/data-error";
import { useMainMenuLabels } from "@/lib/ivr-labels";
import { DashboardView, type DashCall } from "@/components/dashboard-view";

export const Route = createFileRoute("/_app/dashboard")({
  head: () => ({ meta: [{ title: "Dashboard — EESWEB" }] }),
  component: Dashboard,
});

function Dashboard() {
  const { effectiveClientId, role, clientName } = useAuth();
  const clientFilter = effectiveClientId; // null for admin = all
  const { data: menuLabels } = useMainMenuLabels(clientFilter);

  const { data: ivrRoutes } = useQuery({
    queryKey: ["ivr-routes-dashboard", clientFilter],
    queryFn: () => queryDocs<{ key_path: string; main_menu: string; branch_id: string; department_id: string; workshop_type: string }>("ivr_menu_nodes", { clientId: clientFilter }),
  });
  const { data: branches } = useQuery({
    queryKey: ["branches-list-db", clientFilter],
    queryFn: () => queryDocs<{ id: string; name: string }>("branches", { clientId: clientFilter }),
  });
  const { data: departments } = useQuery({
    queryKey: ["departments-list-db", clientFilter],
    queryFn: () => queryDocs<{ id: string; name: string; kind: string }>("departments", { clientId: clientFilter }),
  });

  // One live listener for today's calls plus any still-open call from late last
  // night; live calls and today's totals are both read from it.
  const now = useNow(5000);
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  const todayIso = today.toISOString();
  const sinceIso = new Date(today.getTime() - STALE_LIVE_MS).toISOString();
  const { data: recent, error: callsError } = useCallsSince<DashCall>(clientFilter, sinceIso);
  const todayCalls = useMemo(() => recent?.filter((c) => c.started_at && c.started_at >= todayIso), [recent, todayIso]);
  const live = useMemo(() => (recent ?? []).filter((c) => isReallyLive(c, now)), [recent, now]);

  const ivr = useMemo(
    () => ({ routes: ivrRoutes ?? [], branches: branches ?? [], departments: departments ?? [], menuLabels: menuLabels ?? {} }),
    [ivrRoutes, branches, departments, menuLabels],
  );
  const dateLabel = new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
  const scope = role === "admin" && !clientFilter ? "all dealerships" : clientName ?? null;

  return (
    <>
    <DataError error={callsError} />
    <DashboardView
      title="Dashboard"
      subtitle={scope ? `${dateLabel} · ${scope}` : dateLabel}
      refreshNote="Updates live as calls come in"
      todayCalls={todayCalls}
      liveCalls={live}
      ivr={ivr}
    />
    </>
  );
}
